import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Shuffle } from 'lucide-react';
import PageShell from '../components/PageShell.jsx';
import FeedFilters, { feedFilterCounts, filterFeed } from '../components/FeedFilters.jsx';
import FeedItem from '../components/FeedItem.jsx';
import FeedLiveStatus from '../components/FeedLiveStatus.jsx';
import DayOfLifeHeader from '../components/DayOfLifeHeader.jsx';
import HeroSentence from '../components/HeroSentence.jsx';
import { FeedSkeleton } from '../components/Skeleton.jsx';
import { fetchSnapshot } from '../lib/snapshot.js';
import {
  readFeedCache,
  writeFeedCache,
  isCacheFresh,
  beginRefresh,
  endRefresh,
} from '../lib/feedCache.js';
import { groupByDay } from '../lib/time.js';
import { resolvePds } from '../lib/atproto.js';
import { buildUnifiedFeed } from '../lib/feedBuilder.js';
import { groupSelfReplyThreads, threadAwareDateKey } from '../lib/threadGrouping.js';
import { subscribeRefreshTick } from '../lib/refreshTick.js';
import { ME_DID } from '../config.js';
import '../components/Feed.css';

const FEED_CACHE_KEY = 'unifiedFeed';
const CACHE_TTL_MS = 30_000;
// First-paint cap per collection — matches AT Proto's per-request
// listRecords ceiling, so each collection lands in a single round trip
// with no extra pagination. Background polls keep the same cap; deeper
// history isn't fetched, only `Load more` reveals what's already there.
const INITIAL_FETCH_MAX = 100;
// How many items the feed renders before showing the "Load more" CTA,
// and how many additional items each click reveals.
const INITIAL_VISIBLE = 100;
const LOAD_MORE_STEP = 100;
// Snapshot is now a fallback only (shown when the live fetch errors).
// Cap how much of it we render so an old, fat snapshot doesn't dominate
// the page when the network is flaky.
const SNAPSHOT_FALLBACK_MAX = 60;


/**
 * Collapse `listening` items into session batches. Listens are merged
 * when they fall within `LISTEN_BATCH_GAP_MS` of each other — the gap
 * is measured listen-to-listen, so non-listening items interleaved in
 * the feed (a post mid-session, etc.) don't fragment the batch. Day
 * boundary doesn't matter; a session that crosses midnight still
 * groups into one row.
 */
const LISTEN_BATCH_GAP_MS = 2 * 60 * 60 * 1000; // 2 hours

/**
 * Per-day summary line shown under each day header in the home feed.
 * Counts:
 *   - records  — real underlying records (listen batches expand to
 *                their `count` so a session of 10 songs reads as 10
 *                records, not 1).
 *   - posts    — items whose verb is `posting`.
 *   - engagements — sum of like/repost/reply counts across this day's
 *                   posting items. Reflects how much activity those
 *                   posts attracted on Bluesky.
 * Parts with a zero count are omitted so quieter days read clean.
 */
function dayStatsLine(items) {
  let records = 0;
  let posts = 0;
  let engagements = 0;
  for (const item of items || []) {
    records += item?.count || 1;
    if (item?.verb === 'posting') {
      posts += 1;
      const p = item.payload || {};
      engagements += (p.likeCount || 0) + (p.repostCount || 0) + (p.replyCount || 0);
    }
  }
  const parts = [];
  if (records) parts.push(`${records} ${records === 1 ? 'record' : 'records'}`);
  if (posts) parts.push(`${posts} ${posts === 1 ? 'post' : 'posts'}`);
  if (engagements) parts.push(`${engagements} ${engagements === 1 ? 'engagement' : 'engagements'}`);
  return parts.join(' · ');
}

function collapseListens(items) {
  const out = [];
  let openBatch = null;
  let lastListenTime = 0;
  for (const item of items) {
    if (item.verb === 'listening') {
      const t = Date.parse(item.createdAt) || 0;
      if (openBatch && Math.abs(lastListenTime - t) <= LISTEN_BATCH_GAP_MS) {
        openBatch.count = (openBatch.count || 1) + 1;
        openBatch.plays.push(item);
        lastListenTime = t;
        continue;
      }
      const batch = { ...item, count: 1, plays: [item] };
      openBatch = batch;
      lastListenTime = t;
      out.push(batch);
    } else {
      // Non-listening items pass through to the output but DON'T
      // close the batch — the next listen within the gap window
      // still merges into the same session.
      out.push(item);
    }
  }
  return out;
}

export default function Home() {
  const [params] = useSearchParams();

  // Hydrate synchronously from the in-memory cache so navigating
  // Home → About → Home within the TTL re-renders the feed instantly
  // with no skeleton flash.
  const initialCache = readFeedCache(FEED_CACHE_KEY);
  const [feed, setFeed] = useState(initialCache ? initialCache.items : null);
  const [loadedAt, setLoadedAt] = useState(
    initialCache && initialCache.loadedAt ? new Date(initialCache.loadedAt) : null,
  );
  const [refreshState, setRefreshState] = useState(initialCache ? 'ready' : 'idle');

  const reduce = useReducedMotion();

  // How many items the feed is currently rendering. Starts at
  // INITIAL_VISIBLE and grows by LOAD_MORE_STEP each click of the
  // "Load more" CTA at the bottom of the feed. Resets when the active
  // filter set changes so the user always starts from the top of the
  // new view.
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const filterSig = params.toString();
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [filterSig]);

  // URIs we've already shown the user. Anything missing on a refresh is
  // a "new arrival" and gets the slide-in animation. Seeded from cache
  // so the items we hydrated with don't animate on first paint.
  const seenUrisRef = useRef(new Set());
  // URIs whose `motion.li` should run the slide-in. Cleared after each
  // render via a layout effect (see below).
  const [newUris, setNewUris] = useState(() => new Set());
  if (seenUrisRef.current.size === 0 && initialCache?.items) {
    for (const item of initialCache.items) {
      if (item?.atUri) seenUrisRef.current.add(item.atUri);
    }
  }

  // The active refresh — we hold it on a ref so the polling tick can
  // skip if one is already in flight, and `cancelled` cleanup can mark
  // it stale on unmount.
  const inflightRef = useRef(null);

  // HeroSentence registers its imperative shuffle handle here so the
  // shuffle button in the hero CTA row can advance both rotators
  // without owning the hero's state.
  const shuffleRef = useRef(null);

  const runRefresh = useCallback(async () => {
    if (inflightRef.current) return inflightRef.current;

    const token = { cancelled: false };
    inflightRef.current = token;

    setRefreshState('refreshing');
    beginRefresh();

    // Strategy: live wins. The prebuilt snapshot is a fallback only —
    // we fetch it lazily and only render it if the live fetch fails.
    // (Showing snapshot first then replacing with live made the feed
    // visibly "twitch" as items reordered.)
    let live = null;
    let liveError = null;
    try {
      const pds = await resolvePds(ME_DID);
      const result = await buildUnifiedFeed({
        pds,
        me: ME_DID,
        options: {
          warn: (...args) => console.warn('[home]', ...args),
          initialMax: INITIAL_FETCH_MAX,
          authorFeedMax: INITIAL_FETCH_MAX,
          skipListMembers: true,
        },
      });
      live = Array.isArray(result?.unified) ? result.unified : null;
    } catch (err) {
      liveError = err;
      console.warn('[home] live refresh failed', err);
    }

    if (token.cancelled) {
      if (inflightRef.current === token) inflightRef.current = null;
      endRefresh();
      return null;
    }

    let nextFeed = null;
    let nextRefreshState = 'ready';

    if (live) {
      nextFeed = live;
    } else {
      // Live failed — fall back to the snapshot, capped so a fat old
      // snapshot doesn't dwarf the user's actual "Load more" history.
      const seed = await fetchSnapshot(FEED_CACHE_KEY).catch(() => null);
      if (token.cancelled) {
        if (inflightRef.current === token) inflightRef.current = null;
        endRefresh();
        return null;
      }
      if (Array.isArray(seed)) {
        nextFeed = seed.slice(0, SNAPSHOT_FALLBACK_MAX);
        nextRefreshState = liveError ? 'stale' : 'ready';
      } else {
        nextFeed = [];
        nextRefreshState = liveError ? 'error' : 'ready';
      }
    }

    // Diff against what the user has already seen. New URIs animate in;
    // everything else re-renders in place via Motion's `initial={false}`.
    const arrivals = new Set();
    for (const item of nextFeed) {
      const uri = item?.atUri;
      if (uri && !seenUrisRef.current.has(uri)) arrivals.add(uri);
    }

    const now = new Date();
    setFeed(nextFeed);
    setLoadedAt(now);
    setRefreshState(nextRefreshState);
    // Only flag arrivals on background refreshes — the very first load
    // (when seenUris is still empty) shouldn't slide every row in.
    if (seenUrisRef.current.size > 0 && arrivals.size > 0) {
      setNewUris(arrivals);
    }
    for (const uri of arrivals) seenUrisRef.current.add(uri);

    writeFeedCache(FEED_CACHE_KEY, {
      items: nextFeed,
      loadedAt: now.getTime(),
      fetchedAt: Date.now(),
    });

    if (inflightRef.current === token) inflightRef.current = null;
    endRefresh();
    return nextFeed;
  }, []);

  // Initial load / cache hydration → maybe refresh.
  useEffect(() => {
    if (!isCacheFresh(FEED_CACHE_KEY, CACHE_TTL_MS)) {
      runRefresh();
    }
    return () => {
      if (inflightRef.current) inflightRef.current.cancelled = true;
    };
  }, [runRefresh]);

  // Keep the feed alive on the shared 30s refresh tick — same cadence
  // as NowPlaying and NowStatus, so all the "live" surfaces update
  // together. The tick already skips while the tab is hidden and
  // fires on visibility return, so we don't need separate handlers.
  useEffect(() => subscribeRefreshTick(runRefresh), [runRefresh]);

  // Clear the arrival set once Motion has had a chance to play the
  // entrance animation, so the same items don't re-animate on the next
  // unrelated render (filter change, etc.).
  useEffect(() => {
    if (newUris.size === 0) return undefined;
    const id = setTimeout(() => setNewUris(new Set()), 1200);
    return () => clearTimeout(id);
  }, [newUris]);

  const loading = feed === null;
  const safeFeed = feed || [];

  const counts = useMemo(() => feedFilterCounts(safeFeed, ME_DID), [safeFeed]);

  const filtered = useMemo(() => filterFeed(safeFeed, params, ME_DID), [safeFeed, params]);
  // "Load more" pagination — the user only sees the first `visibleCount`
  // filtered items at a time. Slicing here (before threading / day
  // grouping) means thread chains never get split mid-conversation: each
  // visible window is a clean prefix of the filtered timeline.
  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = filtered.length > visible.length;
  const collapsed = useMemo(() => collapseListens(visible), [visible]);
  const threaded = useMemo(() => groupSelfReplyThreads(collapsed, ME_DID), [collapsed]);
  const groups = useMemo(() => groupByDay(threaded, threadAwareDateKey), [threaded]);

  // Stagger delays among the new arrivals (top-down cascade).
  const arrivalIndex = useMemo(() => {
    const map = new Map();
    let i = 0;
    for (const group of groups) {
      for (const item of group.items) {
        if (item?.atUri && newUris.has(item.atUri)) {
          map.set(item.atUri, i++);
        }
      }
    }
    return map;
  }, [groups, newUris]);

  return (
    <PageShell
      title={<HeroSentence shuffleRef={shuffleRef} />}
      atUri={`at://${ME_DID}/is.dame.page/home`}
      headTitle="Dame is&hellip;"
    >
      <nav className="home-hero-cta" aria-label="Primary destinations">
        <Link className="home-hero-cta-btn home-hero-cta-primary" to="/creating">
          Browse Projects
        </Link>
        <Link className="home-hero-cta-btn" to="/blogging">
          Read Blog
        </Link>
        <button
          type="button"
          className="home-hero-cta-btn home-hero-shuffle"
          onClick={() => shuffleRef.current?.()}
          aria-label="Shuffle the hero phrase"
          title="Shuffle"
        >
          <Shuffle size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </nav>

      <section className="home-latest">
        <FeedFilters counts={counts} />
        {loading ? (
          <FeedSkeleton rows={8} />
        ) : filtered.length === 0 ? (
          <p className="feed-empty">No records match these filters.</p>
        ) : (
          <ol className="feed-list reveal-stagger">
            {groups.map((group, gi) => (
              <li key={group.dateKey} className="feed-day-group">
                <DayOfLifeHeader
                  date={group.date}
                  prefix={gi === 0 ? 'Latest' : null}
                  meta={dayStatsLine(group.items)}
                />
                <ul className="feed-list" style={{ marginTop: 'var(--space-3)' }}>
                  <AnimatePresence initial={false}>
                    {group.items.map((item, i) => {
                      const uri = item?.atUri;
                      const isNew = uri ? newUris.has(uri) : false;
                      const stagger = isNew ? (arrivalIndex.get(uri) ?? 0) * 0.06 : 0;
                      return (
                        <motion.li
                          key={uri || `fallback-${group.dateKey}-${i}`}
                          layout
                          initial={isNew && !reduce ? { opacity: 0, y: -24 } : false}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: reduce ? 0 : 0.45,
                            ease: [0.22, 0.61, 0.36, 1],
                            delay: reduce ? 0 : stagger,
                          }}
                        >
                          <FeedItem item={item} />
                        </motion.li>
                      );
                    })}
                  </AnimatePresence>
                </ul>
              </li>
            ))}
          </ol>
        )}
        {!loading && hasMore && (
          <div className="feed-load-more">
            <button
              type="button"
              className="feed-load-more-btn"
              onClick={() => setVisibleCount((n) => n + LOAD_MORE_STEP)}
            >
              Load more
              <span className="feed-load-more-count">
                {' '}({visible.length} of {filtered.length})
              </span>
            </button>
          </div>
        )}
        {!loading && loadedAt && (
          <FeedLiveStatus
            refreshState={refreshState}
            loadedAt={loadedAt}
            summary={`${visible.length} of ${filtered.length} shown · ${safeFeed.length} loaded`}
          />
        )}
      </section>
    </PageShell>
  );
}
